import conf from '../../conf';
import { setSystemError } from '../../store/system';
import { submitPublicSupportCaseTextAndLink, submitSupportCaseTextAndLink } from '../constants';
import { accountExistsMessage, accountExistsTitle, emailExistsMessage } from '../errors';
import { checkIsAuthenticated, embeddedViewRequested, getAuthLogin } from '../helpers';

import type { Dispatch } from '@avalara/types/actions';
import type { ActiveFlow } from '../../../../types';

const contactUsUrl = 'https://www.avalara.com/us/en/about/contact-us.html';
const embeddedAProps = embeddedViewRequested() ? ' target="_blank" rel="noopener noreferrer"' : '';

type GetSystemErrorMessagingProps = {
  activeFlow?: ActiveFlow;
  errorCode: string;
  isPaid?: boolean;
  locationString?: string;
  msg?: string;
  offerFlow?: string | null;
  offerName?: string;
  partnerName?: string;
  productName?: string;
  returnsEligibilityStatus?: string;
  serverMsg?: string;
};

export const getSystemErrorMessaging =
  (errorData: GetSystemErrorMessagingProps) =>
  async (dispatch: Dispatch): Promise<void> => {
    const {
      errorCode,
      isPaid,
      locationString,
      msg = '',
      offerName,
      partnerName = '',
      productName = '',
      returnsEligibilityStatus,
      serverMsg,
    } = errorData;

    let displaySimpleMessage = false;
    let html: string | undefined;
    let locationAfterErrorDismissal = locationString;
    let message = msg;
    let secondaryMessage = [] as Array<string>;
    let title = '';

    switch (errorCode) {
      case 'accountAlreadyExists':
        title = accountExistsTitle;
        html = accountExistsMessage;
        break;
      case 'defaultIneligibility':
        title = `You're not eligible to buy ${offerName} online`;
        html = `Go to <a href="${conf.AA_URL}"${embeddedAProps}>your account</a> or <a href="${contactUsUrl}"${embeddedAProps}>contact us</a> for help.`;
        locationAfterErrorDismissal = conf.AA_URL;
        break;
      case 'emailAlreadyExists':
        title = accountExistsTitle;
        html = emailExistsMessage(productName);
        break;
      case 'existingPaidAvatax':
        title = 'Account already has AvaTax';
        message = 'This account already has an AvaTax subscription. You will be redirected to Avalara Account';
        secondaryMessage = ['To upgrade your tier, contact us at', '877-224-3650.'];
        locationAfterErrorDismissal = conf.AA_URL;
        break;
      case 'failedAccountFetch':
        title = 'Sorry, something went wrong';
        message = "We couldn't load your account.";
        secondaryMessage = ['Please try again or contact us at', '877-224-3650.'];
        locationAfterErrorDismissal = `${conf.AA_URL}/auth/login`;
        break;
      case 'failedCybersourcePayment':
        title = 'We were unable to charge your payment method.';
        message = msg;
        displaySimpleMessage = true;
        break;
      case 'failedReturnsAccountFetch':
        title = 'Sorry, something went wrong';
        message = 'You must be logged in to continue.';
        secondaryMessage = ['Please try again or contact us at', '877-224-3650.'];
        locationAfterErrorDismissal = getAuthLogin();
        break;
      case 'failedUsageFetch':
        title = 'Something went wrong';
        message = 'Redirecting you to your account page';
        locationAfterErrorDismissal = conf.AA_URL;
        break;
      case 'ineligibleConnector':
        title = "Can't add connector";
        message = "You either already have this connector, or it isn't eligible for this offer";
        locationAfterErrorDismissal = conf.AA_URL;
        break;
      case 'ineligibleForReturnsPurchase':
        title = "You're not eligible to purchase Managed Returns";
        secondaryMessage = ['To upgrade your tier, contact us at', '877-224-3650.'];
        // TODO (Andrew) - confirm where we want to send users in case of this error
        locationAfterErrorDismissal = conf.AA_URL;
        message = (() => {
          switch (returnsEligibilityStatus) {
            case 'NO_PERMANENT_AVATAX':
              return 'You need an AvaTax subscription to purchase Managed Returns';
            case 'ALREADY_HAS_RETURNS':
            case 'ALREADY_HAS_BASIC_RETURNS':
              return 'You already have a returns subscription';
            default:
              return '';
          }
        })();
        break;
      case 'missingConnector':
        title = 'Connector is missing';
        message = "We don't know which connector you want to add";
        locationAfterErrorDismissal = conf.AA_URL;
        break;
      case 'noBillingContact':
        title = 'Sorry, something went wrong';
        message = 'Purchase for this account is not available.';
        secondaryMessage = ['Please try again or contact us at', '877-224-3650.'];
        locationAfterErrorDismissal = `${conf.CLIENT_URL}/auth/logout`;
        break;
      case 'notAvailableForExistingAccounts':
        title = "You can't buy this product online";
        html = `<a href="${conf.AA_URL}"${embeddedAProps}>Contact your account manager</a> <br /> to buy ${offerName || 'this product'}`;
        locationAfterErrorDismissal = conf.AA_URL;
        break;
      case 'prequalQuestionnaireError':
        title = 'We hit a glitch';
        message =
          "We're working to resolve the problem as soon as possible. Upon closing this message, you'll be redirected to avalara.com.";
        locationAfterErrorDismissal = 'https://www.avalara.com/us/en/get-started.html';
        break;
      case 'redirectOutOfSandbox':
        title = 'Attention';
        message = "You're being redirected from your sandbox to a production environment.";
        locationAfterErrorDismissal = `${conf.CLIENT_URL}${getAuthLogin()}`;
        break;
      case 'signupError': {
        const supportTicketLink = checkIsAuthenticated()
          ? submitSupportCaseTextAndLink
          : submitPublicSupportCaseTextAndLink;
        title = 'Sorry, something went wrong';
        message = `${msg}${isPaid ? " Your payment method wasn't charged." : ''}`;
        html = `${supportTicketLink} and we'll look into the problem.`;
        locationAfterErrorDismissal = /email already exists/.test(serverMsg || '') ? conf.AA_URL : '';
        break;
      }
      case 'upsStoreReturnsIneligible':
        title = "You can't buy this product online";
        html = `Your business might be a good fit for a different Avalara Returns product. <a href="${conf.CUP_URL}/returns/upgrade">Get Avalara Managed Returns</a> or <a href="${conf.AA_URL}/#/support-cases">submit a support case</a>.`;
        locationAfterErrorDismissal = conf.AA_URL;
        break;
      case 'unsupportedConnector':
        title = 'Sorry, something went wrong';
        message = 'Purchase for this connector is not currently supported.';
        secondaryMessage = ['Please contact us at', '877-224-3650.'];
        locationAfterErrorDismissal = '/';
        break;
      case 'esbReturnUrlMissing':
        title = `We are unable to return you to ${partnerName}`;
        message = `Your Avalara account was created and will be available when you sign in to ${partnerName}`;
        locationAfterErrorDismissal = '/';
        break;
      case 'ips-api-error':
        title = 'Verifying the purchase failed';
        message = 'Your order ran into an issue. Please do not refresh or try to purchase again';
        break;
      case 'ineligibleForAccountsPayable':
        title = 'Sorry, something went wrong';
        message = 'You must be logged in to continue.';
        secondaryMessage = ['Please log in or contact us at', '877-224-3650.'];
        locationAfterErrorDismissal = `${conf.CLIENT_URL}${getAuthLogin()}`;
        break;
      case 'chiliPiperBookingError':
        title = 'Sorry, something went wrong';
        message = 'There was an error scheduling a call';
        secondaryMessage = ['Please try again or contact us at', '877-224-3650.'];
        break;
      // case 'genericError': same as default
      default:
        title = 'Sorry, something went wrong';
        html = `Please try again or <a href="${contactUsUrl}"${embeddedAProps}>contact us</a> for help.`;
        break;
    }

    dispatch(
      setSystemError({
        message,
        secondaryMessage,
        title,
        html,
        serverMessage: serverMsg,
        locationAfterErrorDismissal,
        displaySimpleMessage,
      }),
    );
  };
